(() => {
  // #region installment fee manipulation methods

  const resolveInstallmentFeeManualPayment = (cart) => {
    const paymentMethod =
      cart?.attributes?._paymentMethod ||
      window.cartDataForPaymentSelectionInCart?.attributes?._paymentMethod ||
      window.cartAvailablePaymentMethods?.subscription?.[0] ||
      'Online';

    return !!(window.KROWN?.settings?.manualPaymentTypes?.includes(paymentMethod));
  };

  // Build the installment-fee cart line item from the values stashed in
  // localStorage by theme.liquid. Pure — no network — so callers can batch the
  // fee into the same /cart/add.js request as the product instead of chaining a
  // separate add. Returns null when no installment fee is configured.
  const buildInstallmentFeeItem = (isManualPayment) => {
    const installmentFeeVariantId = localStorage.getItem('installmentFeeVariantId');
    const installmentFeeVariantSellingPlanId = localStorage.getItem('installmentFeeVariantSellingPlanId');
    const installmentMetafieldValue = localStorage.getItem('installmentMetafieldValue');
    const installmentFeeGlobalId = String(localStorage.getItem('installmentFeeGlobalId') || '').trim();

    if (!installmentFeeVariantId || !installmentFeeVariantSellingPlanId) {
      return null;
    }

    const item = {
      id: installmentFeeVariantId,
      quantity: 1,
      selling_plan: isManualPayment ? '' : installmentFeeVariantSellingPlanId,
      properties: {}
    };

    if (installmentMetafieldValue) {
      item.properties["_monthly_base_total"] = installmentMetafieldValue;
    }

    if (isManualPayment) {
      item.properties["_sellingplan"] = installmentFeeVariantSellingPlanId;
    }

    if (typeof window.mergeCartLinePropertiesWithGlobalId === 'function') {
      item.properties = window.mergeCartLinePropertiesWithGlobalId(
        installmentFeeVariantId,
        item.properties
      );
    }
    if (!item.properties._globalId && installmentFeeGlobalId) {
      item.properties._globalId = installmentFeeGlobalId;
    }

    return item;
  };

  const postInstallmentFeeItem = async (isManualPayment) => {
    const item = buildInstallmentFeeItem(isManualPayment);
    if (!item) {
      return false;
    }

    if (window.JPCartService) {
      try {
        // The service waits on the shared-cart restore gate itself.
        await window.JPCartService.add({ items: [item] });
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    }

    if (typeof window.awaitSharedCartRestore === 'function') {
      await window.awaitSharedCartRestore();
    }

    const response = await fetch(`${KROWN.settings.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ items: [item] })
    }).catch((error) => {
      console.error(error);
      return null;
    });

    return !!(response && response.ok);
  };

  const addInstallmentFee = async (isManualPayment) => {
    // remove before adding so that quantity is always 1
    await removeInstallmentFee();
    await postInstallmentFeeItem(isManualPayment);
  };

  const removeInstallmentFee = async () => {
    const installmentFeeVariantId = localStorage.getItem('installmentFeeVariantId');

    if (!installmentFeeVariantId) {
      return;
    }

    if (typeof window.awaitSharedCartRestore === 'function') {
      await window.awaitSharedCartRestore();
    }

    const updates = {};
    updates['' + installmentFeeVariantId] = 0;

    try {
      // update.js returns the full updated cart, so we read the empty-state
      // straight from the mutation response instead of a follow-up /cart request.
      const cart = window.JPCartService
        ? await window.JPCartService.update({ updates })
        : await fetch(`${KROWN.settings.routes.cart_update_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates })
          }).then((r) => r.json());

      if (cart && Array.isArray(cart.items)) {
        $(".countries_list_modal-content").attr('data-cart-empty', cart.items.length === 0 ? "true" : "false");
      }
    } catch (error) {
      console.error('There was a problem updating the cart:', error);
    }
  };

  const isInstallmentFeeConfigured = () =>
    !!(localStorage.getItem('installmentFeeVariantId') &&
      localStorage.getItem('installmentFeeVariantSellingPlanId'));

  const getInstallmentFeeVariantId = () => {
    const id = parseInt(localStorage.getItem('installmentFeeVariantId') || '', 10);
    return Number.isFinite(id) ? id : null;
  };

  const getInstallmentFeeLines = (cart) => {
    const feeId = getInstallmentFeeVariantId();
    if (!feeId || !cart?.items) {
      return [];
    }
    return cart.items.filter((item) => Number(item.variant_id ?? item.id) === feeId);
  };

  const cartHasInstallmentFee = (cart) => getInstallmentFeeLines(cart).length > 0;

  const readCartForInstallmentFee = async ({ force = false } = {}) => {
    if (window.JPCartService) {
      if (!force) {
        const peeked = window.JPCartService.peek?.();
        if (peeked?.items) {
          return peeked;
        }
      }
      return window.JPCartService.getCart({ force: !!force });
    }

    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }
    return response.json();
  };

  const changeInstallmentFeeLineQuantity = async (lineKey, quantity) => {
    if (!lineKey) {
      return false;
    }

    if (window.JPCartService) {
      await window.JPCartService.change({ id: lineKey, quantity });
      return true;
    }

    if (typeof window.awaitSharedCartRestore === 'function') {
      await window.awaitSharedCartRestore();
    }

    const response = await fetch(`${KROWN.settings.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity })
    });
    return response.ok;
  };

  // Enforce exactly one installment-fee line at quantity 1 (cross-tab race safe).
  // No-op when this market has no fee config. Call after installment ATC success.
  const ensureInstallmentFeeQuantityOne = async (isManualPayment) => {
    if (!isInstallmentFeeConfigured()) {
      return false;
    }

    let cart = await readCartForInstallmentFee({ force: true });
    let feeLines = getInstallmentFeeLines(cart);
    let changed = false;

    if (feeLines.length > 1) {
      for (let i = 1; i < feeLines.length; i++) {
        await changeInstallmentFeeLineQuantity(feeLines[i].key, 0);
        changed = true;
      }
      cart = await readCartForInstallmentFee({ force: true });
      feeLines = getInstallmentFeeLines(cart);
    }

    if (feeLines.length === 1 && feeLines[0].quantity > 1) {
      await changeInstallmentFeeLineQuantity(feeLines[0].key, 1);
      return true;
    }

    if (feeLines.length === 0) {
      const manual =
        typeof isManualPayment === 'boolean'
          ? isManualPayment
          : resolveInstallmentFeeManualPayment(cart);
      return !!(await postInstallmentFeeItem(manual));
    }

    return changed;
  };

  // #endregion

  if (typeof window !== 'undefined') {
    window.addInstallmentFee = addInstallmentFee;
    window.removeInstallmentFee = removeInstallmentFee;
    window.buildInstallmentFeeItem = buildInstallmentFeeItem;
    window.postInstallmentFeeItem = postInstallmentFeeItem;
    window.resolveInstallmentFeeManualPayment = resolveInstallmentFeeManualPayment;
    window.cartHasInstallmentFee = cartHasInstallmentFee;
    window.isInstallmentFeeConfigured = isInstallmentFeeConfigured;
    window.ensureInstallmentFeeQuantityOne = ensureInstallmentFeeQuantityOne;
  }
})();
